import * as React from "react";
import { GoogleMap } from "@react-google-maps/api";
import { useOkHiIncomingMessage } from "./OkHiIncomingMessageProvider";
import { Flex } from "@chakra-ui/layout";
import { fetchStreetViewPano, fetchUserLocation, getZoomLevel } from "../utils";
import Icon from "@chakra-ui/icon";
import { Button, IconButton } from "@chakra-ui/button";
import { useHistory } from "react-router";
import {
  useAddressGeopoint,
  useAddressStreetviewData,
} from "./AddressStateProvider";
import { useAcquireAddressState } from "./AcquireAddressStateProvider";
import { useDisclosure } from "@chakra-ui/hooks";
import { BiCurrentLocation } from "react-icons/bi";
import { useToast } from "@chakra-ui/toast";
import { GrMapLocation } from "react-icons/gr";
import { MdOutlineSatellite } from "react-icons/md";
import { okhiTracker } from "../OkHiTracker";
import { LightMode } from "@chakra-ui/react";
import { MapDropPin } from "./MapDropPin";
import { useUserLocation } from "../hooks/useUserLocation";
import { useDebouncedCallback } from "use-debounce/lib";

export function MapScreen() {
  const toastId = "geolocservice-unavailable";
  const toast = useToast();
  const { userLocationSnapshot } = useUserLocation();
  //const mapTypeBtnBg = useColorModeValue("white", "gray.800");
  /* const { isOpen: isNotifOpen } = useDisclosure({
    defaultIsOpen: true,
  }); */
  const { isOpen: isTooltipOpen, onClose: onCloseTooltip } = useDisclosure({
    defaultIsOpen: true,
  });
  const { setAcquireAddressState } = useAcquireAddressState();
  const { setAddressStreetviewData } = useAddressStreetviewData();
  const { setAddressGeoPoint, addressGeoPoint } = useAddressGeopoint();
  const [center, setCenter] = React.useState<google.maps.LatLngLiteral | null>(
    addressGeoPoint
  );
  const okhiMessage = useOkHiIncomingMessage();
  const [zoom, setZoom] = React.useState<number>(() => {
    if (
      okhiMessage?.type === "atAddressImpreciseGps" ||
      okhiMessage?.type === "atAddressPreciseGps"
    ) {
      const geoPointAccuracy = okhiMessage.geoPointAccuracy;
      return getZoomLevel(geoPointAccuracy);
    }
    return 19;
  });
  const history = useHistory();
  const [showErrorMessage, setShowErrorMessage] = React.useState(true);
  const [mapInstance, setMapInstance] = React.useState<google.maps.Map | null>(
    null
  );
  const [loadingStreetviewPano, setLoadingStreetviewPano] =
    React.useState(false);
  const [acquiringUserLocation, setAcquiringUserLocation] =
    React.useState(false);
  const [mapTypeId, setMapTypeId] = React.useState<"roadmap" | "satellite">(
    "roadmap"
  );
  const [userLocationMarker, setUserLocationMarker] =
    React.useState<google.maps.Marker | null>(null);
  const [userLocationCircle, setUserLocationCircle] =
    React.useState<google.maps.Circle | null>(null);
  const isButtonNext =
    okhiMessage?.context.isStreetviewEnabled ||
    okhiMessage?.context.isToTheDoorEnabled;
  const onMapLoad = React.useCallback((mapInstance: google.maps.Map) => {
    setMapInstance(mapInstance);
  }, []);
  const handleMapInteraction = () => {
    if (isTooltipOpen) {
      onCloseTooltip();
    }
    if (showErrorMessage) {
      setShowErrorMessage(false);
    }
  };
  const handleDrag = useDebouncedCallback(() => {
    const center = mapInstance?.getCenter();
    if (center != null) {
      setCenter(center.toJSON());
      handleMapInteraction();
    }
  }, 300);
  //Manage user location marker
  React.useEffect(() => {
    if (mapInstance == null || userLocationSnapshot.location == null) {
      return;
    }
    if (userLocationMarker == null) {
      setUserLocationMarker(
        new window.google.maps.Marker({
          map: mapInstance,
          position: userLocationSnapshot.location,
          title: "User Location",
          icon: {
            path: "M0.5 7C0.5 3.41015 3.41015 0.5 7 0.5C10.5899 0.5 13.5 3.41015 13.5 7C13.5 10.5899 10.5899 13.5 7 13.5C3.41015 13.5 0.5 10.5899 0.5 7Z",
            fillColor: "#0087D3",
            fillOpacity: 1,
            strokeColor: "white",
            strokeOpacity: 1,
            scale: 1,
            rotation: 0,
            anchor: new window.google.maps.Point(7, 7),
          },
        })
      );
    } else {
      userLocationMarker.setMap(mapInstance);
      userLocationMarker.setPosition(userLocationSnapshot.location);
    }
  }, [
    mapInstance,
    userLocationMarker,
    setUserLocationMarker,
    userLocationSnapshot,
  ]);
  //Manage user accuracy radius
  React.useEffect(() => {
    if (mapInstance == null || userLocationSnapshot.location == null) {
      return;
    }
    if (userLocationCircle == null) {
      setUserLocationCircle(
        new window.google.maps.Circle({
          map: mapInstance,
          center: userLocationSnapshot.location,
          radius: userLocationSnapshot.location.accuracy,
          fillColor: "#0087D3",
          fillOpacity: 0.3,
          strokeWeight: 0,
        })
      );
    } else {
      userLocationCircle.setMap(mapInstance);
      userLocationCircle.setCenter(userLocationSnapshot.location);
      userLocationCircle.setRadius(userLocationSnapshot.location.accuracy);
    }
  }, [
    mapInstance,
    userLocationCircle,
    setUserLocationCircle,
    userLocationSnapshot,
  ]);
  React.useEffect(() => {
    okhiTracker.page();
  }, []);
  const handlePanToLocation = async () => {
    setAcquiringUserLocation(true);
    try {
      const location = await fetchUserLocation();
      mapInstance?.panTo({
        lat: location.coords.latitude,
        lng: location.coords.longitude,
      });
    } catch (err) {
      if (!toast.isActive(toastId)) {
        toast({
          id: toastId,
          title: "Geolocation service unavailable",
          status: "error",
          position: "bottom",
          duration: 3000,
        });
      }
    }
    setAcquiringUserLocation(false);
  };
  const handleSaveLocation = async () => {
    const center = mapInstance?.getCenter()!;
    const geoPoint = { lat: center.lat(), lng: center.lng() };
    setAddressStreetviewData(null);
    setAddressGeoPoint(geoPoint);
    if (okhiMessage?.context.isStreetviewEnabled) {
      try {
        setLoadingStreetviewPano(true);
        const streetviewData = await fetchStreetViewPano(
          geoPoint.lat,
          geoPoint.lng
        );
        const panoId = streetviewData.location?.pano;
        if (panoId != null) {
          setAddressStreetviewData({ panoId });
          history.push("/streetview");
          okhiTracker.track("Button Tapped", {
            action: "tapclick",
            onObject: "button",
            label: "Next",
          });
          return;
        }
      } catch (err) {
        console.error(err);
        console.error("failed to fetch streetview panorama");
      }
    }
    if (okhiMessage?.context.isToTheDoorEnabled) {
      history.push("/to-the-door");
      okhiTracker.track("Button Tapped", {
        action: "tapclick",
        onObject: "button",
        label: "Next",
      });
      return;
    }
    setAcquireAddressState("complete");
    okhiTracker.track("Button Tapped", {
      action: "tapclick",
      onObject: "button",
      label: "Done",
    });
  };
  const handleMapTypeToggle = () => {
    setMapTypeId(mapTypeId === "roadmap" ? "satellite" : "roadmap");
  };
  return (
    <Flex w="100%" h="100%" flexDir="column" position="relative">
      {/* <Fade in={isNotifOpen} unmountOnExit={true}>
        <Alert>
          <BsArrowsMove />
          <AlertDescription ml={4}>
            Move the pin to your address
          </AlertDescription>
        </Alert>
      </Fade> */}
      <Flex flex="1" position="relative">
        <GoogleMap
          mapContainerStyle={{
            width: "100%",
            flex: 1,
          }}
          options={{
            gestureHandling: "greedy",
            streetViewControl: false,
            zoomControl: false,
            fullscreenControl: false,
            mapTypeControl: false,
            mapTypeId,
            tilt: 0,
            rotateControl: false,
            clickableIcons: false,
            /* mapTypeControlOptions: {
            mapTypeIds: [
              google.maps.MapTypeId.ROADMAP,
              google.maps.MapTypeId.SATELLITE,
            ],
            position: google.maps.ControlPosition.TOP_RIGHT,
            style: google.maps.MapTypeControlStyle.DEFAULT,
          }, */
          }}
          center={addressGeoPoint || undefined}
          zoom={zoom}
          onLoad={onMapLoad}
          onDrag={handleDrag}
          onClick={handleMapInteraction}
          onZoomChanged={() => {
            const zoom = mapInstance?.getZoom();
            if (zoom != null && center != null) {
              mapInstance?.panTo(center);
              setZoom(zoom);
            }
          }}></GoogleMap>
        <MapDropPin isTooltipOpen={isTooltipOpen} />
        <LightMode>
          <Button
            position="absolute"
            top={4}
            right="2.5%"
            leftIcon={
              mapTypeId === "roadmap" ? (
                <MdOutlineSatellite />
              ) : (
                <GrMapLocation />
              )
            }
            onClick={handleMapTypeToggle}
            variant="outline"
            background="white"
            color="gray.800"
            shadow="base">
            {mapTypeId === "roadmap" ? "Satellite" : "Map"}
          </Button>
          <IconButton
            position="absolute"
            right="2.5%"
            bottom="100"
            icon={
              <Icon as={BiCurrentLocation} color="gray.600" fontSize="1.5rem" />
            }
            isLoading={acquiringUserLocation}
            aria-label="Locate on Map"
            size="lg"
            borderRadius="50%"
            variant="outline"
            background="white"
            shadow="base"
            onClick={handlePanToLocation}
          />
        </LightMode>
        <Button
          isLoading={loadingStreetviewPano}
          loadingText="Saving"
          variant="okhi-solid"
          shadow="base"
          position="absolute"
          bottom="32px"
          width="95%"
          mx="auto"
          left="0"
          right="0"
          p={6}
          disabled={!addressGeoPoint}
          onClick={handleSaveLocation}>
          {isButtonNext ? "Next" : "Done"}
        </Button>
      </Flex>
    </Flex>
  );
}
